/**
 * O convite de meta compartilhada atravessando o login.
 *
 * ── A armadilha, e ela é a mesma de `reciboDoAviso.ts` ───────────────────
 *
 * `AuthContext` faz `signInWithOAuth({ redirectTo: window.location.origin })`.
 * `origin` é **só o domínio**: o caminho não volta do Google.
 *
 * E aqui isso é pior do que foi lá. O convite de meta é o único fluxo do app
 * desenhado para quem NÃO TEM CONTA (`REGRAS_DA_META.exigeConta = false`) —
 * ou seja, a pessoa que chega quase sempre vai passar pelo cadastro. Sem
 * guardar o token antes, TODO convidado novo voltaria do Google no painel
 * vazio, sem nunca ter entrado na meta, e sem nada explicando o que aconteceu.
 *
 * O convite continuaria pendente no banco, o link ainda valeria, e a pessoa
 * não teria motivo nenhum para clicar nele de novo.
 *
 * ── Por que `sessionStorage` e não `localStorage` ────────────────────────
 *
 * Um token de convite que sobrevive ao fechar do navegador é um token que
 * entra numa meta semanas depois, sozinho, no primeiro login. `sessionStorage`
 * morre com a aba, que é exatamente a vida útil da ida ao Google e da volta.
 */

const CHAVE = 'fintracking:convite-de-meta';

/** O mesmo formato de `rotaPublica.ts`: 32 bytes em hex. */
const HEX = /^[0-9a-f]{64}$/;

export const CAMINHO_DO_CONVITE_DE_META = 'convite-meta';

/**
 * O token na URL, se o caminho for o do convite de meta.
 *
 * Validado por FORMATO, e isso não é segurança — é para `/convite-meta/` sem
 * token não abrir uma tela que nunca vai carregar. Quem autoriza é o servidor,
 * que compara o SHA-256.
 */
export const rotaDeConviteDeMeta = (pathname: string): string | null => {
  const partes = String(pathname ?? '').split('/').filter(Boolean);
  if (partes.length !== 2) return null;
  const [secao, token] = partes;
  if (secao !== CAMINHO_DO_CONVITE_DE_META) return null;
  return HEX.test(token) ? token : null;
};

/**
 * Guarda o token para atravessar o login.
 *
 * O `try` existe porque `sessionStorage` LANÇA em aba anônima com cookies
 * bloqueados. Perder o convite ali é ruim; quebrar o carregamento do app é
 * pior — e a pessoa ainda tem o link no email.
 */
export const guardarConviteDeMeta = (token: string): void => {
  try {
    if (HEX.test(token)) window.sessionStorage.setItem(CHAVE, token);
  } catch { /* ver acima */ }
};

export const conviteDeMetaPendente = (): string | null => {
  try {
    const t = window.sessionStorage.getItem(CHAVE);
    return t && HEX.test(t) ? t : null;
  } catch {
    return null;
  }
};

/**
 * Apaga o pendente.
 *
 * Chamado quando a pessoa ENTRA na meta e também quando ela decide não entrar
 * — nos dois casos o convite foi respondido. Sem o segundo, quem clicou em
 * "agora não" voltaria para a mesma tela no próximo carregamento da aba,
 * porque o pendente é o que faz o `index.tsx` desviar para cá.
 */
export const limparConviteDeMeta = (): void => {
  try {
    window.sessionStorage.removeItem(CHAVE);
  } catch { /* ver acima */ }
};
